import { useState, useEffect, useCallback } from 'react';
import { isAutoFitDebugActive, addDebugLog } from '../../ui/components/debug/DebugUtils';

interface UseAutoTextFitOptions {
    minFontSize?: number;
    maxFontSize?: number;
    lineHeight?: number;
    padding?: number;
}

interface UseAutoTextFitResult {
    fontSize: number;
    isAdjusting: boolean;
    ref: React.RefObject<HTMLDivElement>;
}

/**
 * Hook para ajustar dinámicamente el tamaño de fuente de un contenedor
 * para que el contenido quepa sin hacer scroll.
 */
export const useAutoTextFit = (
    content: React.ReactNode,
    {
        minFontSize = 12,
        maxFontSize = 100,
        lineHeight = 1.4,
        padding = 0,
    }: UseAutoTextFitOptions = {}
): UseAutoTextFitResult => {
    const [fontSize, setFontSize] = useState(maxFontSize);
    const [isAdjusting, setIsAdjusting] = useState(true);
    const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null);

    // Ref callback para manejar la inicialización del nodo
    const ref = useCallback((node: HTMLDivElement) => {
        setContainerRef(node);
    }, []);

    useEffect(() => {
        if (!containerRef) return;

        const adjustFontSize = () => {
            const startTime = performance.now();
            setIsAdjusting(true);

            const container = containerRef;
            if (!container) return;

            // Encontrar el contenedor interno con el contenido real
            const contentWrapper = container.querySelector('#auto-fit-content-wrapper') as HTMLElement;
            if (!contentWrapper) {
                setIsAdjusting(false);
                return;
            }

            // Resetear estilos para medición limpia
            container.style.fontSize = `${maxFontSize}px`;
            container.style.lineHeight = `${lineHeight}`;

            // Altura disponible real - restar padding y un margen de seguridad de 20px fijo
            const containerRect = container.getBoundingClientRect();
            const safetyPadding = (padding > 0 ? padding : 40) + 20;
            const availableHeight = containerRect.height - safetyPadding;

            // Logging inicial
            if (isAutoFitDebugActive()) {
                addDebugLog('useAutoTextFit', 'Iniciando ajuste de fuente', {
                    minFontSize,
                    maxFontSize,
                    availableHeight,
                    lineHeight,
                    padding: safetyPadding,
                });
            }

            // Si no hay altura disponible (ej: oculto), salir
            if (availableHeight <= 0) {
                if (isAutoFitDebugActive()) {
                    addDebugLog('useAutoTextFit', 'Sin altura disponible, abortando', {
                        availableHeight,
                    });
                }
                setIsAdjusting(false);
                return;
            }

            // Función helper para verificar overflow REAL contra la altura disponible
            const checkOverflow = (): boolean => {
                // Forzar reflow
                void container.offsetHeight;
                void contentWrapper.offsetHeight;

                const contentHeight = contentWrapper.scrollHeight;

                // Verificar si el contenido excede el espacio reservado (incluyendo el margen de seguridad)
                return contentHeight > availableHeight;
            };

            // Algoritmo de búsqueda binaria para encontrar el tamaño MÁXIMO que quepa
            let low = minFontSize;
            let high = maxFontSize;
            let optimalSize = minFontSize;

            // Iteraciones
            let iterations = 0;
            const maxIterations = 40;

            while (low <= high && iterations < maxIterations) {
                const mid = Math.floor((low + high) / 2);
                container.style.fontSize = `${mid}px`;

                if (checkOverflow()) {
                    // Si hay overflow, necesitamos reducir la fuente
                    high = mid - 1;
                } else {
                    // Si cabe completamente, guardamos este tamaño como candidato y probamos uno mayor
                    optimalSize = mid;
                    low = mid + 1;
                }
                iterations++;
            }

            // Verificación final absoluta: asegurar que el tamaño óptimo realmente cabe
            container.style.fontSize = `${optimalSize}px`;

            // Si aún hay overflow (puede ocurrir por redondeo decimal), reducir de 1 en 1
            if (checkOverflow() && optimalSize > minFontSize) {
                let safeSize = optimalSize;
                while (safeSize > minFontSize && checkOverflow()) {
                    safeSize -= 1;
                    container.style.fontSize = `${safeSize}px`;
                }
                optimalSize = safeSize;
            }

            // Un último toque de seguridad de 1px extra para evitar bordes de corte en pantallas de TV
            if (optimalSize > minFontSize + 1) {
                optimalSize -= 1;
                container.style.fontSize = `${optimalSize}px`;
            }


            // Aplicar tamaño final
            const finalContainerRect = container.getBoundingClientRect();
            const finalContentHeight = contentWrapper.scrollHeight;
            const finalContainerHeight = finalContainerRect.height;
            const finalHasOverflow = checkOverflow();
            const executionTime = performance.now() - startTime;

            // Logging final
            if (isAutoFitDebugActive()) {
                addDebugLog('useAutoTextFit', 'Ajuste de fuente completado', {
                    optimalSize,
                    finalContentHeight,
                    finalContainerHeight,
                    finalHasOverflow,
                    executionTime: `${executionTime.toFixed(2)}ms`,
                    iterations,
                    minFontSize,
                    maxFontSize,
                });
            }

            setFontSize(optimalSize);
            container.style.fontSize = `${optimalSize}px`;
            setIsAdjusting(false);
        };

        // Observer para cambios de tamaño en el contenedor
        const resizeObserver = new ResizeObserver(() => {
            // Usar requestAnimationFrame para evitar errores de "ResizeObserver loop limit exceeded"
            requestAnimationFrame(adjustFontSize);
        });

        resizeObserver.observe(containerRef);

        // Ejecutar ajuste inicial
        adjustFontSize();

        return () => {
            resizeObserver.disconnect();
        };
    }, [containerRef, content, minFontSize, maxFontSize, lineHeight, padding]);

    return { fontSize, isAdjusting, ref: ref as any };
};
